import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BasicPageNavControls from "../../publicFunctions/clientClasses/BasicPageNavControls";
import BasicPageNav from "../BasicPageNav";
import LoadingSection from "../LoadingSection";
import { Timestamp } from "firebase/firestore";
import { fetchReports, fetchTopics } from "../../publicFunctions/forumHandler";
import {
  DEFAULT_FORUM_LIST_SIZE,
  mapStalePagination,
  pageScrollSettings,
} from "../../publicFunctions/publicHelpers/helpers";
import TopicItem from "./TopicItem";
import Report from "../reports/Report";

function TopicList({
  searchParams,
  setSearchParams,
  forceRefresh,
  handleNotActive,
  stalePaginationArray,
  nightMode,
  mutedUsers,
  reports = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const topRef = useRef(null);

  const [fullReset, setFullReset] = useState(false);
  const [forceRefreshNav, setForceRefreshNav] = useState(false);
  const [noMoreTopics, setNoMoreTopics] = useState(false);
  const [renderedTopics, setRenderedTopics] = useState([]);
  const [isWorking, setIsWorking] = useState(false);
  const [pageNavControls, setPageNavControls] = useState(
    new BasicPageNavControls(
      "/forum",
      "topicsNum",
      "topicsIndex",
      null,
      mapStalePagination(stalePaginationArray)
    )
  );

  useEffect(() => {
    if (searchParams.get("page") === "topics" && mutedUsers) {
      loadTopics();
    }
  }, [searchParams, forceRefresh, nightMode, mutedUsers]);

  async function loadTopics() {
    setIsWorking(true);
    if (topRef && searchParams?.get("topicsIndex")) {
      topRef.current.scrollIntoView(pageScrollSettings);
    }
    try {
      let lastReplyDate = null;
      const currentIndex = searchParams.get("topicsIndex");
      if (currentIndex && currentIndex !== "none") {
        lastReplyDate = Timestamp.fromMillis(parseInt(currentIndex));
      }

      const topics = reports
        ? await fetchReports()
        : await fetchTopics(lastReplyDate);

      if (topics.length === DEFAULT_FORUM_LIST_SIZE + 1) {
        const pagination = pageNavControls.pagination;
        const lastReplyDate = topics[DEFAULT_FORUM_LIST_SIZE - 1].lastReplyDate;
        const milliseconds = Math.floor(
          lastReplyDate.seconds * 1000 +
            Math.ceil(lastReplyDate.nanoseconds / 1000000)
        );
        const nextIndex = milliseconds.toString(10);
        const numString = searchParams.get("topicsNum");
        if (currentIndex && currentIndex !== "none" && numString) {
          pagination.set(numString, currentIndex);
        }

        // since we're here, we know that there IS more pagination.
        if (pagination?.size && pagination.size === parseInt(numString)) {
          // add another page
          pagination.set((pagination.size + 1).toString(10), nextIndex);
          setForceRefreshNav(!forceRefreshNav);
        }

        setPageNavControls(
          new BasicPageNavControls(
            pageNavControls.defaultNavigateString,
            pageNavControls.numParamName,
            pageNavControls.indexParamName,
            nextIndex,
            pagination
          )
        );
        setNoMoreTopics(false);
      } else if (topics.length <= DEFAULT_FORUM_LIST_SIZE) {
        setNoMoreTopics(true);
      }

      if (topics?.length && mutedUsers) {
        const tempRender = [];
        for (let i = 0; i < topics.length && i < DEFAULT_FORUM_LIST_SIZE; i++) {
          const topic = topics[i];
          if (topic?.userId && mutedUsers && mutedUsers[topic.userId]) {
            continue;
          }

          if (reports) {
            tempRender.push(
              <Report
                key={topic.objectId}
                nightMode={nightMode}
                reportObject={topic}
                searchParams={searchParams}
                setSearchParams={setSearchParams}
              />
            );
          } else {
            tempRender.push(
              <TopicItem
                key={topic.topicId}
                topic={topic}
                nightMode={nightMode}
                mutedUsers={mutedUsers}
                searchParams={searchParams}
                setSearchParams={setSearchParams}
              />
            );
          }
        }
        setRenderedTopics(tempRender);
      }
    } catch (err) {
      console.log(err);
      setNoMoreTopics(true);
      if (err?.message?.includes("insufficient permissions")) {
        handleNotActive();
      }
    } finally {
      setIsWorking(false);
    }
  }

  return (
    <div>
      <div ref={topRef} className="simple-divider"></div>

      {/* TOP NAV */}
      {renderedTopics?.length && !reports ? (
        <BasicPageNav
          pageNum={
            searchParams.has("topicsNum")
              ? parseInt(searchParams.get("topicsNum"))
              : 1
          }
          noMorePages={noMoreTopics}
          incrementPageHandler={() => {
            pageNavControls.incrementPageHandler(searchParams, setSearchParams);
          }}
          decrementPageHandler={() => {
            pageNavControls.decrementPageHandler(
              location,
              navigate,
              searchParams,
              setSearchParams
            );
          }}
          goToPageHandler={(page, index) => {
            pageNavControls.goToPage(
              page,
              index,
              searchParams,
              setSearchParams
            );
          }}
          isWorking={isWorking}
          pagination={pageNavControls.pagination}
          showPageDropdown={true}
          forceRefreshNav={forceRefreshNav}
          nightMode={nightMode}
        />
      ) : null}

      {/* PAYLOAD */}
      <div key={fullReset} className={"topics-list"}>
        {!isWorking ? renderedTopics : <LoadingSection nightMode={nightMode} />}
      </div>

      {/* BOTTOM NAV */}
      {renderedTopics?.length && !reports ? (
        <BasicPageNav
          pageNum={
            searchParams.has("topicsNum")
              ? parseInt(searchParams.get("topicsNum"))
              : 1
          }
          noMorePages={noMoreTopics}
          incrementPageHandler={() => {
            pageNavControls.incrementPageHandler(searchParams, setSearchParams);
          }}
          decrementPageHandler={() => {
            pageNavControls.decrementPageHandler(
              location,
              navigate,
              searchParams,
              setSearchParams
            );
          }}
          goToPageHandler={(page, index) => {
            pageNavControls.goToPage(
              page,
              index,
              searchParams,
              setSearchParams
            );
          }}
          isWorking={isWorking}
          pagination={pageNavControls.pagination}
          showPageDropdown={true}
          forceRefreshNav={forceRefreshNav}
          nightMode={nightMode}
        />
      ) : null}
    </div>
  );
}

export default TopicList;
